import styled from 'styled-components'
import _ from 'lodash'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import InfiniteScroll from 'react-infinite-scroller'
import moment from 'moment'

import useTranslate from '../hooks/useTranslate'
import useMounted from '../hooks/useMounted'
import media from '../config/theme/media'
import { useNews, useHasMore, useIsLoading } from '../hooks/newsHooks'
import { useWatchlistNews as useWatchlistNewsSdk, useWatchlistHasMore as useWatchlistHasMoreSdk, useLatestWatchlistNews as useLatestWatchlistNewsSdk, useLatestWatchlistHasMore as useLatestWatchlistHasMoreSdk, useLatestProducttNews as useLatestProductNewsSdk, useLatestProductHasMore as useLatestProductHasMoreSdk, useWatchlistIsLoading as useWatchlistIsLoadingSdk, useLatestWatchlistIsLoading as useLatestWatchlistIsLoadingSdk, useLatestProductIsLoading as useLatestProductIsLoadingSdk } from '../hooks/newsSdkHooks'
import { useWebsiteConfig } from '../hooks/configHooks'
import { actions as newsActions } from '../store/modules/news'
import { actions as newsActionsSdk } from '../store/modules/newsSdk'

import H3 from '../components/Typography/H3'
import P from '../components/Typography/P'
import Link from '../components/Link'
import Icon from '../components/Icon'
import Loader from '../components/Loader'

const NewsList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding-left: 0;
  width: 100%;
  max-width: 100%;
  h2 {
    margin-bottom: 0;
  }
`

const NewsLink = styled(Link)`
  cursor: pointer;
  display: flex;
  h2 {
    color: #1d70b8;
    text-decoration: underline;
    margin-top: 0;
    margin-bottom: 5px;
    display: inline-block;
  }
  span {
    display: inline-block;
    margin-left: 5px;
  }
`
const LoadingMessage = styled(P)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px 0;
`
const StyledInfiniteScroll = styled(InfiniteScroll)`
  width: 100%;
`
const StyledDescription = styled(P)`
  margin-top: 0;
  color: #505a5f;
  ${media.greaterThan('md')`
    margin-top: 5px;
  `}
`

const Source = styled(P)`
  min-width: 150px;
  margin-top: 0;
  font-size: 10px;
  color: #505a5f;
  ${media.greaterThan('md')`
    font-size: 16px;
  `}
`

const Tags = styled(P)`
  display: flex;
  align-items: center;
  justify-content: left;
  margin-top: 0;
  font-size: 10px;
  color: #505a5f;
  ${media.greaterThan('md')`
    font-size: 16px;
  `}
`

const NewsDetail = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const ModifiedAt = styled(P)`
  min-width: 200px;
  text-align: right;
  margin-top: 0;
  font-size: 10px;
  color: #505a5f;
  ${media.greaterThan('md')`
    font-size: 16px;
  `}
`

const NotFoundStyle = styled(P)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  padding-right: 20px;
`

const NoNewsText = styled.div`
  padding-right: 20px;
  text-align: left;
  padding-top: 10px;
  padding-bottom: 10px;
`

const IconStyle = styled.div`
  padding-right: 8px;
`

const Badge = styled.div`
    color: #fff;
    background-color: grey;
    display: inline-block;
    padding: 0.25em 0.4em;
    font-size: 80%;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: 0.25rem;
    margin-right: 5px;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
`

const TagsHeading = styled.div`
  text-align: right;
`

const TagsList = styled.ul`
  padding-left: 5px;
  text-align: left;
  white-space: nowrap;
`

const Tag = styled.li`
  list-style-type: none;
  display: inline;
`

const News = ({ useSdk = false, watchlist = false, usersWatchlist, allResult = true, drugName, watchlistArray = []}) => {
  const NewsListItem = styled.li`
    ${({ allResult }) => allResult
      ? 'border-top: 1px solid #b1b4b6;'
      : `&:first-child {
        border-top: 0px;
      }`
    }
    
    border-top: 1px solid #b1b4b6;

    padding-top: 20px;
    padding-bottom: 10px;
    margin: 0.5em 0;
    font-weight: normal;
    line-height: 16px;
    font-size: 12px;
    ${media.greaterThan('md')`
      font-size: 16px;
      line-height: 24px;
    `}
  `

  const translate = useTranslate()
  const dispatch = useDispatch()
  const router = useRouter()
  const news = useSdk? (allResult? useWatchlistNewsSdk(): ( drugName? useLatestProductNewsSdk(): useLatestWatchlistNewsSdk() )): useNews()
  const hasMoreNews = useSdk? (allResult? useWatchlistHasMoreSdk(): ( drugName? useLatestProductHasMoreSdk(): useLatestWatchlistHasMoreSdk() )) : useHasMore()
  const isLoadingNews = useSdk? (allResult? useWatchlistIsLoadingSdk(): ( drugName? useLatestProductIsLoadingSdk(): useLatestWatchlistIsLoadingSdk() )): useIsLoading()
  const [isMounted] = useMounted()
  const newsType = allResult? "watchlistNews": (drugName? "productLatestWidget":"watchlistLatestWidget")
  const viewNewsArticle = ({ newsItem }) => {
    useSdk? (dispatch(newsActionsSdk.resetStore(newsType))): dispatch(newsActions.resetStore())
    router.push({
      pathname: `/news/${newsItem.id}`,
    })
  }

  const NewsDetailBlock = ({ newsItem }) => {
    const source = _.get(newsItem, 'newsFeed.source')
    const tags = _.get(newsItem, 'associatedDrugs', [])
    const publishedAt = _.get(newsItem, 'publishedDate')
    const updatedAt = _.get(newsItem, 'updatedAt')

    const isUpdated = moment(updatedAt).isAfter(moment(publishedAt))

    const filteredTags = tags.filter(tag => { return watchlistArray.indexOf(tag.toUpperCase()) > -1 })

    return (
      <NewsDetail>
        <Source><div dangerouslySetInnerHTML={{ __html: translate('NEWS_SOURCE', { source }) }} /></Source>
        {filteredTags.length > 0 && 
        <Tags>
          <TagsHeading/>{translate('NEWS_TAG')}:
          <TagsList>
            {filteredTags.map((tag) => { return (<Tag><Badge>{tag.toUpperCase()}</Badge></Tag> )} )}
          </TagsList>
        </Tags>
        }
        <ModifiedAt>{`${isUpdated ? translate('NEWS_UPDATED') : translate('NEWS_PUBLISHED')
          }: ${moment(isUpdated ? updatedAt : publishedAt).format(
            'D MMMM, YYYY'
          )}`}</ModifiedAt>
      </NewsDetail>
    )
  }

  return (
    <>
      {isMounted ? (
        <StyledInfiniteScroll
          loadMore={() => useSdk? dispatch(newsActionsSdk.fetchNewsNext({ searchTerm: '', watchlist, usersWatchlist, newsType, size: newsType=='watchlistNews'? 10: 2})): dispatch(newsActions.fetchNewsNext({ searchTerm: '' }))}
          hasMore={!isLoadingNews && hasMoreNews && allResult}
          initialLoad={false}
          loader={
            <LoadingMessage className="loader" key={0}>
              {translate('NEWS_LISTING_LOADING')}
            </LoadingMessage>
          }
        >
          <NewsList>
            {_.map(news, (item) => {
              const isInternal = _.get(item, 'newsFeed.isInternalFeed')

              return (
                <NewsListItem key={item.id} allResult={allResult}>
                  <NewsLink
                    href={isInternal ? `/news/${item.id}` : item.url}
                    newPageIfExternal={!isInternal}
                    onClick={() =>
                      isInternal && viewNewsArticle({ newsItem: item })
                    }
                  >
                    <H3 as={'H2'}>
                      {item.title}
                      {!isInternal && (
                        <span>
                          <Icon name={'newTab'} aria-hidden size={14} />
                        </span>
                      )}
                    </H3>
                  </NewsLink>
                  <StyledDescription>{item.description}</StyledDescription>
                  <NewsDetailBlock newsItem={item}/>
                </NewsListItem>
              )
            })}
            {news?.length === 0 ? (
              <NotFoundStyle>
                <IconStyle><Icon name='info' size={25}/></IconStyle>
                <NoNewsText>{allResult?translate('NO_NEWS_TEXT'):<div dangerouslySetInnerHTML={{ __html: drugName?translate('NO_NEWS_LATEST_PRODUCT_TEXT') : translate('NO_NEWS_LATEST_WATCHLIST_TEXT')} } />}</NoNewsText>
              </NotFoundStyle>
            )
            : null}
          </NewsList>
        </StyledInfiniteScroll>
      ) : (
        <Loader />
      )}
    </>
  )
}

export default News
